import { Injectable, OnDestroy, signal } from '@angular/core';
import { Subscription, take } from 'rxjs';

import { AuthService } from './auth.service';
import { FirebaseService } from './firebase.service';
import { S3FileService } from './s3-file.service';
import {
  ChatConversation,
  ChatMessage,
  ChatService,
  ChatUser,
  PendingChatAttachment,
} from './chat.service';

@Injectable({ providedIn: 'root' })
export class ChatWidgetStateService implements OnDestroy {
  readonly open = signal(false);
  readonly loading = signal(false);
  readonly sending = signal(false);
  readonly uploading = signal(false);
  readonly unreadCount = signal(0);
  readonly conversations = signal<ChatConversation[]>([]);
  readonly activeConversationId = signal<string | null>(null);
  readonly thread = signal<ChatMessage[]>([]);
  readonly userSearch = signal('');
  readonly userResults = signal<ChatUser[]>([]);
  readonly pendingAttachments = signal<PendingChatAttachment[]>([]);
  readonly avatarUrls = signal<Record<string, string | null>>({});
  readonly error = signal<string | null>(null);

  private refreshTimer?: ReturnType<typeof setInterval>;
  private refreshing = false;
  private selectionVersion = 0;
  private readonly acknowledgedDeliveries = new Set<string>();
  private initialized = false;
  private currentUserId = '';
  private pageVisible = false;
  private readonly requestedAvatarKeys = new Set<string>();
  private readonly firebaseSubscription: Subscription;

  constructor(
    private readonly chatService: ChatService,
    private readonly authService: AuthService,
    private readonly firebase: FirebaseService,
    private readonly files: S3FileService,
  ) {
    this.firebaseSubscription = this.firebase.events$.subscribe((event) => {
      if (event.kind !== 'chat' && event.event !== 'chat.updated') return;
      void this.handleChatEvent(event.conversation_id || null);
    });
  }

  ngOnDestroy(): void {
    this.firebaseSubscription.unsubscribe();
    clearInterval(this.refreshTimer);
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;
    try {
      const user: any = await this.authService.getUser();
      this.currentUserId = String(user?.id || user?.user_id || '').trim();
    } catch {
      this.currentUserId = '';
    }
    await Promise.all([this.loadConversations(), this.refreshUnreadCount()]);
    this.firebase.start(true);
    // Receipts must also update when browser push is unavailable or denied.
    this.refreshTimer = setInterval(() => { void this.refreshVisibleThread(); }, 10000);
  }

  toggle(): void {
    const shouldOpen = !this.open();
    this.open.set(shouldOpen);
    if (shouldOpen) {
      void this.initialize().then(() => this.refreshVisibleThread());
    }
  }

  show(): void {
    if (this.open()) return;
    this.open.set(true);
    void this.initialize().then(() => this.refreshVisibleThread());
  }

  close(): void {
    this.open.set(false);
  }

  setPageVisible(visible: boolean): void {
    this.pageVisible = visible;
    if (visible && this.initialized) void this.refreshVisibleThread();
  }

  async loadConversations(showLoading = true): Promise<void> {
    if (showLoading) this.loading.set(true);
    this.error.set(null);
    try {
      const page = await this.chatService.listConversations(1, 100);
      const items = page.items || [];
      this.conversations.set(items);
      this.resolveAvatars(items.flatMap((item) => item.participants.map((participant) => participant.user)));
      // Keep the header badge useful even when the aggregate endpoint is
      // temporarily unavailable. Each conversation carries its own unread
      // message count, so this is a reliable client-side fallback.
      this.unreadCount.set(
        items.reduce((total, item) => total + Math.max(0, Number(item.unread_count || 0)), 0),
      );
      const activeId = this.activeConversationId();
      if (activeId && items.some(item => item.id === activeId)) return;
      if (items.length > 0) await this.selectConversation(items[0].id);
    } catch {
      this.error.set('Unable to load conversations.');
    } finally {
      if (showLoading) this.loading.set(false);
    }
  }

  async searchUsers(query: string): Promise<void> {
    this.userSearch.set(query);
    try {
      this.userResults.set(await this.chatService.searchUsers(query, 25));
      this.resolveAvatars(this.userResults());
    } catch {
      this.userResults.set([]);
      this.error.set('Unable to search people.');
    }
  }

  async startDirectConversation(userId: string): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const conversation = await this.chatService.createDirectConversation(userId);
      this.conversations.update(items => [
        conversation,
        ...items.filter(item => item.id !== conversation.id),
      ]);
      this.userSearch.set('');
      this.userResults.set([]);
      await this.selectConversation(conversation.id);
    } catch {
      this.error.set('Unable to start the conversation.');
    } finally {
      this.loading.set(false);
    }
  }

  async startGroupConversation(title: string, userIds: string[]): Promise<boolean> {
    const cleanTitle = title.trim();
    const participants = [...new Set(userIds.filter(Boolean))];
    if (!cleanTitle || participants.length === 0) return false;
    this.loading.set(true);
    this.error.set(null);
    try {
      const conversation = await this.chatService.createGroupConversation(cleanTitle, participants);
      this.conversations.update(items => [
        conversation,
        ...items.filter(item => item.id !== conversation.id),
      ]);
      this.userSearch.set('');
      this.userResults.set([]);
      await this.selectConversation(conversation.id);
      return true;
    } catch {
      this.error.set('Unable to create the group conversation.');
      return false;
    } finally {
      this.loading.set(false);
    }
  }

  async selectConversation(conversationId: string): Promise<void> {
    if (!conversationId) return;
    const version = ++this.selectionVersion;
    if (this.activeConversationId() !== conversationId) {
      this.thread.set([]);
      this.pendingAttachments.set([]);
    }
    this.activeConversationId.set(conversationId);
    this.loading.set(true);
    this.error.set(null);
    try {
      const page = await this.chatService.listMessages(conversationId, 1, 100);
      if (version !== this.selectionVersion) return;
      this.thread.set(page.items || []);
      this.resolveAvatars(this.thread().map((message) => message.sender));
      await this.acknowledgeMessages(page.items || []);
      if (version !== this.selectionVersion || !this.isThreadVisible()) return;
      await this.chatService.markRead(conversationId);
      this.conversations.update(items =>
        items.map(item => item.id === conversationId ? { ...item, unread_count: 0 } : item),
      );
      await this.refreshUnreadCount();
    } catch {
      if (version === this.selectionVersion) this.error.set('Unable to load messages.');
    } finally {
      if (version === this.selectionVersion) this.loading.set(false);
    }
  }

  async send(content: string): Promise<boolean> {
    const conversationId = this.activeConversationId();
    const trimmed = content.trim();
    const attachments = this.pendingAttachments();
    if (!conversationId || (!trimmed && attachments.length === 0) || this.sending() || this.uploading()) return false;
    this.sending.set(true);
    this.error.set(null);
    try {
      const message = await this.chatService.sendMessage(conversationId, trimmed, attachments);
      if (this.activeConversationId() === conversationId) {
        this.thread.update(items => [...items.filter(item => item.id !== message.id), message]);
        this.pendingAttachments.set([]);
      }
      await this.loadConversations(false);
      return true;
    } catch {
      this.error.set('Message could not be sent.');
      return false;
    } finally {
      this.sending.set(false);
    }
  }

  async addAttachment(file: File): Promise<void> {
    const conversationId = this.activeConversationId();
    if (!file || !conversationId || this.uploading() || this.sending()) return;
    if (file.size < 1 || file.size > 25 * 1024 * 1024) {
      this.error.set('Choose a file between 1 byte and 25 MB.');
      return;
    }
    this.uploading.set(true);
    this.error.set(null);
    try {
      const attachment = await this.chatService.uploadAttachment(file);
      if (this.activeConversationId() === conversationId) {
        this.pendingAttachments.update(items => [...items, attachment]);
      }
    } catch {
      this.error.set('Attachment upload failed.');
    } finally {
      this.uploading.set(false);
    }
  }

  removePendingAttachment(objectKey: string): void {
    this.pendingAttachments.update(items => items.filter(item => item.object_key !== objectKey));
  }

  async openAttachment(attachmentId: string): Promise<void> {
    try {
      const url = await this.chatService.attachmentDownloadUrl(attachmentId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      this.error.set('Attachment could not be opened.');
    }
  }

  activeConversation(): ChatConversation | null {
    const id = this.activeConversationId();
    return this.conversations().find(item => item.id === id) || null;
  }

  conversationLabel(conversation: ChatConversation): string {
    if (conversation.title) return conversation.title;
    const other = conversation.participants.find(item => item.user.id !== this.currentUserId);
    return other?.user.display_name || 'Conversation';
  }

  isOwnMessage(message: ChatMessage): boolean {
    return message.sender.id === this.currentUserId;
  }

  avatarUrl(user: ChatUser): string | null {
    return this.avatarUrls()[user.id] || null;
  }

  conversationAvatar(conversation: ChatConversation): string | null {
    const other = conversation.participants.find((item) => item.user.id !== this.currentUserId);
    return other ? this.avatarUrl(other.user) : null;
  }

  async refreshUnreadCount(): Promise<void> {
    try {
      this.unreadCount.set(await this.chatService.unreadCount());
    } catch {
      // Keep the last successful count while the client is temporarily offline.
    }
  }


  deliveryStatus(message: ChatMessage): 'Sent' | 'Delivered' | 'Read' {
    const receipts = message.receipts || [];
    if (receipts.length && receipts.every(receipt => !!receipt.read_at)) return 'Read';
    if (receipts.length && receipts.every(receipt => !!receipt.delivered_at || !!receipt.read_at)) return 'Delivered';
    return 'Sent';
  }

  private isThreadVisible(): boolean {
    return (this.open() || this.pageVisible) && document.visibilityState === 'visible';
  }

  private async acknowledgeMessages(messages: ChatMessage[]): Promise<void> {
    await Promise.all(messages.filter(message => !this.isOwnMessage(message) && !message.deleted_at)
      .map(async message => {
        const ownReceipt = message.receipts?.find(receipt => receipt.user_id === this.currentUserId);
        if (ownReceipt?.delivered_at || ownReceipt?.read_at || this.acknowledgedDeliveries.has(message.id)) return;
        await this.chatService.markDelivered(message.conversation_id, message.id);
        this.acknowledgedDeliveries.add(message.id);
      }));
  }

  private async refreshVisibleThread(): Promise<void> {
    const conversationId = this.activeConversationId();
    if (!conversationId || !this.isThreadVisible() || this.refreshing || this.loading() || this.sending()) return;
    this.refreshing = true;
    const version = this.selectionVersion;
    try {
      const page = await this.chatService.listMessages(conversationId);
      if (version !== this.selectionVersion || this.sending()) return;
      this.thread.set(page.items || []);
      await this.acknowledgeMessages(page.items || []);
      if (version !== this.selectionVersion || !this.isThreadVisible()) return;
      if (page.items.some(message => !this.isOwnMessage(message) &&
        message.receipts?.some(receipt => receipt.user_id === this.currentUserId && !receipt.read_at))) {
        await this.chatService.markRead(conversationId);
        await this.loadConversations(false);
      }
    } catch {
      // Preserve the thread during a temporary network failure; the next refresh retries.
    } finally {
      this.refreshing = false;
    }
  }

  private async handleChatEvent(conversationId: string | null): Promise<void> {
    await this.loadConversations(false);
    if (conversationId) {
      try {
        const page = await this.chatService.listMessages(conversationId);
        await this.acknowledgeMessages(page.items || []);
      } catch { /* Opening the conversation retries delivery acknowledgement. */ }
    }
    if (
      conversationId &&
      conversationId === this.activeConversationId() &&
      (this.open() || this.pageVisible)
    ) {
      await this.selectConversation(conversationId);
      return;
    }
    await this.refreshUnreadCount();
  }

  private resolveAvatars(users: ChatUser[]): void {
    for (const user of users) {
      const key = user.profile_image_key?.trim();
      if (!key || this.requestedAvatarKeys.has(key)) continue;
      this.requestedAvatarKeys.add(key);
      this.files.getFileUrlValue(key).pipe(take(1)).subscribe({
        next: (url) => this.avatarUrls.update((urls) => ({ ...urls, [user.id]: url })),
        error: () => this.avatarUrls.update((urls) => ({ ...urls, [user.id]: null })),
      });
    }
  }
}
